import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DYNAMIC_EVENT_DESIGNS,
  EVENTS_CLIENT_URL,
  getDesignName,
} from "./dynamicEventDesigns";

const PREVIEW_PATH = "/__preview";
const PREVIEW_READY_MESSAGE = "lm-preview-ready";
const PREVIEW_EVENT_MESSAGE = "lm-dynamic-event-preview";

const VIEWPORTS = [
  { id: "desktop", label: "Desktop", width: "100%", icon: "fa-desktop" },
  { id: "mobile", label: "Mobile", width: "390px", icon: "fa-mobile-screen" },
];

/**
 * Live preview of the public event page, rendered by events-client itself
 * (iframe to /__preview) and fed the draft event via postMessage. Any design
 * registered in events-client works without copying code into the dashboard.
 *
 * Props:
 *   getEventObj()      -> current draft event (from the form)
 *   translations       -> saved translations for the event (for language tabs)
 *   designId, abTestEnabled, abTestDesignIds -> from the design picker
 *   initialDesignId / initialLang -> optional, e.g. when "Preview" is clicked
 *     on a design card or a language row
 */
export default function DynamicEventPreview({
  getEventObj,
  translations = [],
  designId = "original",
  abTestEnabled = false,
  abTestDesignIds = [],
  initialDesignId,
  initialLang,
  refreshKey,
}) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [previewDesignId, setPreviewDesignId] = useState(initialDesignId || designId);
  const [previewLang, setPreviewLang] = useState(initialLang || "en");
  const [viewport, setViewport] = useState("desktop");
  const [loadError, setLoadError] = useState(false);

  const previewUrl = `${EVENTS_CLIENT_URL}${PREVIEW_PATH}`;
  const eventsClientOrigin = useMemo(() => {
    try {
      return new URL(EVENTS_CLIENT_URL).origin;
    } catch (error) {
      return "*";
    }
  }, []);

  useEffect(() => {
    if (initialDesignId) setPreviewDesignId(initialDesignId);
  }, [initialDesignId]);
  useEffect(() => {
    if (initialLang) setPreviewLang(initialLang);
  }, [initialLang]);

  // Follow the picker: when A/B is off and the chosen design *changes*, show
  // it. Skips the initial mount so an explicit initialDesignId wins.
  const prevDesignIdRef = useRef(designId);
  useEffect(() => {
    if (prevDesignIdRef.current === designId) return; // no actual change
    prevDesignIdRef.current = designId;
    if (!abTestEnabled) setPreviewDesignId(designId);
  }, [designId, abTestEnabled]);

  const postEvent = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    let event;
    try {
      event = getEventObj ? getEventObj() : null;
    } catch (error) {
      console.error("DynamicEventPreview - getEventObj failed", error);
      return;
    }
    if (!event) return;
    frame.contentWindow.postMessage(
      {
        type: PREVIEW_EVENT_MESSAGE,
        event: {
          ...event,
          eventId: event.eventId || "preview",
          translations: Array.isArray(translations) ? translations : [],
        },
        designId: previewDesignId,
        lang: previewLang,
      },
      eventsClientOrigin
    );
  }, [getEventObj, translations, previewDesignId, previewLang, eventsClientOrigin]);

  // Wait for the iframe to say hello, then start pushing the draft.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data && e.data.type === PREVIEW_READY_MESSAGE) {
        if (eventsClientOrigin !== "*" && e.origin !== eventsClientOrigin) return;
        setReady(true);
        setLoadError(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [eventsClientOrigin]);

  // Push on every change (debounced) once ready.
  useEffect(() => {
    if (!ready) return undefined;
    const handle = setTimeout(postEvent, 250);
    return () => clearTimeout(handle);
  }, [ready, postEvent, refreshKey]);

  // If the iframe never reports ready, show a hint (wrong URL / not deployed).
  useEffect(() => {
    const handle = setTimeout(() => {
      if (!ready) setLoadError(true);
    }, 12000);
    return () => clearTimeout(handle);
  }, [ready]);

  const designTabs = DYNAMIC_EVENT_DESIGNS;
  const inRotation = (id) => abTestEnabled && abTestDesignIds.includes(id);
  const languageTabs = [
    { lang: "en", label: "English" },
    ...(Array.isArray(translations) ? translations : []).map((t) => ({
      lang: t.lang,
      label: t.label || t.lang.toUpperCase(),
      stale: !!t.stale,
    })),
  ];
  const frameWidth = VIEWPORTS.find((v) => v.id === viewport)?.width || "100%";

  return (
    <div className="dynamic-event-preview">
      <div className="dynamic-event-preview-toolbar">
        <div className="dep-group" role="tablist" aria-label="Design">
          <span className="dep-label">Design</span>
          {designTabs.map((d) => (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={previewDesignId === d.id}
              className={`dep-tab ${previewDesignId === d.id ? "is-active" : ""} ${
                inRotation(d.id) ? "is-rotation" : ""
              }`}
              onClick={() => setPreviewDesignId(d.id)}
              title={inRotation(d.id) ? "In A/B rotation" : undefined}
            >
              {d.name}
              {inRotation(d.id) ? <span className="dep-dot" aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
        {languageTabs.length > 1 ? (
          <div className="dep-group" role="tablist" aria-label="Language">
            <span className="dep-label">Language</span>
            {languageTabs.map((l) => (
              <button
                key={l.lang}
                type="button"
                role="tab"
                aria-selected={previewLang === l.lang}
                className={`dep-tab ${previewLang === l.lang ? "is-active" : ""}`}
                onClick={() => setPreviewLang(l.lang)}
                title={l.stale ? "Out of date — regenerate" : l.label}
              >
                {l.lang.toUpperCase()}
                {l.stale ? <i className="fa-solid fa-triangle-exclamation dep-stale" /> : null}
              </button>
            ))}
          </div>
        ) : null}
        <div className="dep-group" role="tablist" aria-label="Viewport">
          {VIEWPORTS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={viewport === v.id}
              className={`dep-tab dep-tab--icon ${viewport === v.id ? "is-active" : ""}`}
              onClick={() => setViewport(v.id)}
              title={v.label}
            >
              <i className={`fa-solid ${v.icon}`} />
            </button>
          ))}
          <button
            type="button"
            className="dep-tab dep-tab--icon"
            onClick={postEvent}
            title="Refresh preview"
          >
            <i className="fa-solid fa-rotate-right" />
          </button>
        </div>
      </div>

      <div className={`dynamic-event-preview-stage is-${viewport}`}>
        {!ready ? (
          <div className="dep-status">
            {loadError ? (
              <>
                <b>Preview not responding.</b> The preview loads the public site at{" "}
                <code>{previewUrl}</code>. Make sure that build includes the preview route (or set{" "}
                <code>REACT_APP_EVENTS_CLIENT_URL</code> when running locally).
              </>
            ) : (
              <>Loading preview of the {getDesignName(previewDesignId)} design…</>
            )}
          </div>
        ) : null}
        <iframe
          ref={iframeRef}
          title="Event page preview"
          src={previewUrl}
          style={{ width: frameWidth }}
          className={ready ? "is-ready" : ""}
          onLoad={() => {
            // ready message arrives from the app once it mounts
          }}
        />
      </div>
    </div>
  );
}
